import { z } from 'zod';
import { daySchema } from './common';

export const programInput = z.object({
  nameHr: z.string().min(2).max(160),
  nameEn: z.string().max(160).nullable().optional(),
  descriptionHr: z.string().max(4000).nullable().optional(),
  descriptionEn: z.string().max(4000).nullable().optional(),
  level: z.number().int().min(1).max(5).nullable().optional(),
  isTemplate: z.boolean().default(true),
});

export const weekInput = z.object({
  labelHr: z.string().max(120).nullable().optional(),
  labelEn: z.string().max(120).nullable().optional(),
});

export const workoutInput = z.object({
  dayIndex: z.number().int().min(0).max(6),
  nameHr: z.string().min(1).max(160),
  nameEn: z.string().max(160).nullable().optional(),
  type: z.enum(['strength', 'cardio', 'mobility', 'rest', 'mixed']).default('strength'),
  estimatedMinutes: z.number().int().min(1).max(300).nullable().optional(),
  noteHr: z.string().max(2000).nullable().optional(),
  noteEn: z.string().max(2000).nullable().optional(),
});

export const workoutExerciseInput = z.object({
  exerciseId: z.string().uuid(),
  sets: z.number().int().min(1).max(20).nullable().optional(),
  /** Slobodan tekst jer trener piše "8–12" ili "do otkaza". */
  reps: z.string().max(40).nullable().optional(),
  weightHintKg: z.number().min(0).max(1000).nullable().optional(),
  restSeconds: z.number().int().min(0).max(900).nullable().optional(),
  tempo: z.string().max(20).nullable().optional(),
  trainerNoteHr: z.string().max(1000).nullable().optional(),
  trainerNoteEn: z.string().max(1000).nullable().optional(),
  supersetGroup: z.string().max(10).nullable().optional(),
  prescribedSets: z
    .array(
      z.object({
        reps: z.string().max(40).nullable().optional(),
        weightKg: z.number().min(0).max(1000).nullable().optional(),
        restSeconds: z.number().int().min(0).max(900).nullable().optional(),
        note: z.string().max(200).nullable().optional(),
      }),
    )
    .max(20)
    .optional(),
});

/**
 * Redoslijed vježbi se ne mijenja pojedinačnim PATCH-evima nego zamjenom
 * cijele liste — nema stanja u kojem dvije vježbe imaju isti sortOrder.
 */
export const setWorkoutExercisesInput = z.object({
  exercises: z.array(workoutExerciseInput).max(40),
});

export const exerciseInput = z.object({
  slug: z.string().min(2).max(80).regex(/^[a-z0-9-]+$/, 'Samo mala slova, brojke i crtice'),
  nameHr: z.string().min(1).max(160),
  nameEn: z.string().min(1).max(160),
  category: z.enum([
    'chest', 'back', 'shoulders', 'arms', 'legs', 'glutes', 'core', 'cardio', 'mobility', 'full_body',
  ]),
  muscleGroups: z.array(z.string().max(40)).max(10).default([]),
  equipment: z.string().max(60).nullable().optional(),
  /** Sklek nema kilazu, plank nema ponavljanja — vidi `exerciseLoadTypes`. */
  loadType: z.enum(['weighted', 'bodyweight', 'timed']).default('weighted'),
  difficulty: z.enum(['beginner', 'intermediate', 'advanced']).nullable().optional(),
  instructionsHr: z.string().max(4000).nullable().optional(),
  instructionsEn: z.string().max(4000).nullable().optional(),
  videoKey: z.string().max(300).nullable().optional(),
  imageKey: z.string().max(300).nullable().optional(),
});

export const assignProgramInput = z.object({
  clientId: z.string().uuid(),
  startDate: daySchema,
  /** Gasi prethodnu aktivnu dodjelu istog klijenta. */
  replaceActive: z.boolean().default(true),
});

export type ProgramInput = z.infer<typeof programInput>;
export type WorkoutInput = z.infer<typeof workoutInput>;
export type WorkoutExerciseInput = z.infer<typeof workoutExerciseInput>;
export type ExerciseInput = z.infer<typeof exerciseInput>;
export type AssignProgramInput = z.infer<typeof assignProgramInput>;
